import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { useTranslation } from 'react-i18next'

import { Text, VStack } from '@enechain/ecloud-designsystem'

import { MultiSelect } from '~/components/common/MultiSelect'
import { TradeSideRadioGroup } from '~/components/common/TradeSideRadioGroup'
import type { SharedBidListFilter } from '~/pages/SpotBidding/models/sharedBidListFilter'
import {
  SPOT_BID_STATUS_FILTER_CATEGORIES,
  type SpotBidStatusCategory,
} from '~/pages/SpotBidding/models/spotBidStatusCategory'

type SideRadioValue = '' | 'buy' | 'sell'

const SIDES_OF_RADIO: Record<SideRadioValue, ReadonlySet<Side>> = {
  '': new Set(),
  buy: new Set([Side.BUY]),
  sell: new Set([Side.SELL]),
}

// 値は Set (空 = すべて) で持ち、 部品は design の「すべて / 買 / 売」 の radio (#12355 で Des が部品の型を
// 決めるまで今の radio のまま)。 両方選んだ Set は radio では「すべて」 と同じ絞り込みなので「すべて」 に出す。
const radioValueOf = (sides: ReadonlySet<Side>): SideRadioValue => {
  if (sides.size !== 1) return ''
  return sides.has(Side.BUY) ? 'buy' : 'sell'
}

const isStatusFilterCategory = (
  value: string,
): value is SpotBidStatusCategory =>
  (SPOT_BID_STATUS_FILTER_CATEGORIES as readonly string[]).includes(value)

type Props = {
  value: SharedBidListFilter
  onChange: (next: SharedBidListFilter) => void
}

/**
 * 通常入札タブ・ブロック入札タブのサイドバーに置く、 両タブで共有する絞り込み (AC-BSP-019 の売/買・
 * ステータス)。 両タブが同じ部品を描き、 値は page が持つ 1 つの状態を受け取る。
 */
export const SharedBidListFilterFields = ({
  value,
  onChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <>
      <VStack align="stretch" gap={1}>
        {/* design ルール例外: filter label は Text.highEmphasis 固定 (design/CLAUDE.md) */}
        <Text textStyle="filterLabel" color="Text.highEmphasis">
          {t('page:biddingSpot.sidebar.filterSide')}
        </Text>
        <TradeSideRadioGroup
          allLabel={t('page:biddingSpot.sidebar.filterSideAll')}
          ariaLabel={t('page:biddingSpot.sidebar.filterSide')}
          name="spot-bid-side-filter"
          onChange={(next) =>
            onChange({ ...value, sides: SIDES_OF_RADIO[next] })
          }
          value={radioValueOf(value.sides)}
          values={{ all: '', buy: 'buy', sell: 'sell' }}
        />
      </VStack>

      {/* 選択肢は AC-BSP-019a の 11 区分。 [2026-09-29 デザイナー指摘] checkbox つきの multi-select は
          DS に無いので、 DS 準拠の `MultiSelect` (chip + typeahead) を使う。 */}
      <VStack align="stretch" gap={1}>
        {/* design ルール例外: filter label は Text.highEmphasis 固定 (design/CLAUDE.md) */}
        <Text textStyle="filterLabel" color="Text.highEmphasis">
          {t('page:biddingSpot.sidebar.filterStatus')}
        </Text>
        <MultiSelect
          aria-label={t('page:biddingSpot.sidebar.filterStatus')}
          onChange={(next) =>
            onChange({
              ...value,
              statuses: new Set(next.filter(isStatusFilterCategory)),
            })
          }
          options={SPOT_BID_STATUS_FILTER_CATEGORIES.map((category) => ({
            value: category,
            label: t(`page:biddingSpot.bidStatusCategory.${category}`),
          }))}
          placeholder={t('page:biddingSpot.sidebar.filterStatusAll')}
          selected={[...value.statuses]}
        />
      </VStack>
    </>
  )
}
